import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import * as actions from './actions';
import { useActions } from 'hooks/useActions';
import CompanySettings from './CompanySettings';
import DepartmentSettings from './DepartmentSettings';
import EmailTemplateSettings from './EmailTemplateSettings';
import TeamSettings from './TeamSettings';
import PrivacySettings from './PrivacySettings';
import IntegrationSettings from './IntegrationSettings';
import RecruitmentStagesSettings from './RecruitmentStagesSettings';
import CandidateRatingWeightsSettings from './CandidateRatingWeightsSettings';
import CandidateRatingAreasSettings from './CandidateRatingAreasSettings';
import DemoAccountBanner from '../../components/DemoAccountBanner';
import { Wrapper, Button, Container } from '../../components';
import AnchorContent from '../../components/AnchorContent/anchorContent';
import { TopHeadingContainer, Header, AnchorLink } from './styles';
import { getConfig } from 'functions/getConfig';
import { Spinner } from 'components/Spinner/spinner';
import { userRoles } from 'util/appConstants';

export default React.memo(() => {
  const config = getConfig();
  const { loadSettings, resetUpdatedIds, updateCompany } = useActions(actions);

  let {
    fetchSettings,
    error,
    settings,
    updateCompanySuccess,
    updatedDepartmentId,
    newDepartmentId,
    agreementId,
    leverUserId,
    TTUserId,
  } = useSelector((state) => state.settings);
  let { userRole, company } = useSelector((state) => state.auth);

  useEffect(() => {
    if (error && !error.includes('internal error')) toast.error(error);
  }, [error]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  useEffect(() => {
    if (updateCompanySuccess) {
      toast.success('Company updated successfully');
      resetUpdatedIds();
    }
  }, [updateCompanySuccess]);

  useEffect(() => {
    if (updatedDepartmentId) {
      toast.success('Department updated successfully');
      resetUpdatedIds();
    }
  }, [updatedDepartmentId]);

  useEffect(() => {
    if (newDepartmentId) toast.success('Department created successfully');
    resetUpdatedIds();
  }, [newDepartmentId]);

  useEffect(() => {
    if (agreementId) toast.success('New privacy agreement published');
    resetUpdatedIds();
  }, [agreementId]);

  useEffect(() => {
    if (TTUserId) toast.success('TeamTailor integration user successfully created');
    resetUpdatedIds();
  }, [TTUserId]);

  useEffect(() => {
    if (leverUserId) toast.success('Lever integration information successfully updated');
    resetUpdatedIds();
  }, [leverUserId]);

  if (userRole) {
    if (![userRoles.SUPER_ADMIN, userRoles.COMPANY_ADMIN, userRoles.RESELLER].includes(userRole))
      window.location.href = `${config.REDIRECTION_URL}/dashboard`;
  }

  let anchorPoints = [
    {
      label: 'Company',
      component: settings.company && <CompanySettings userRole={userRole} />,
      icon: 'fa fa-fort-awesome',
    },
    {
      label: 'Department',
      component: settings.departments && <DepartmentSettings />,
      icon: 'fa fa-building',
    },
    {
      label: 'Team',
      component: settings.teamMembers && <TeamSettings />,
      icon: 'fa fa-group',
    },
    {
      label: 'Email Template',
      component: settings.emails && <EmailTemplateSettings />,
      icon: 'fa fa-envelope',
    },
    {
      label: 'Privacy',
      component: settings.GDPRAgreement && <PrivacySettings userRole={userRole} companyId={company?._id} />,
      icon: 'fa fa-shield',
    },
    {
      label: 'Integrations',
      component: settings.company && <IntegrationSettings />,
      icon: 'fa fa-cogs',
    },
  ];

  let anchorPointsForNonTestsOnlyCompany = [
    {
      label: 'Recruitment Stages',
      component: settings?.company?.stages && (
        <RecruitmentStagesSettings
          recruitmentStages={settings.company.stages}
          updateStages={updateCompany}
          id={settings.company._id}
        />
      ),
      icon: 'fa fa-arrow-right',
    },
    {
      label: 'Candidate Ratings',
      component: settings.company && (
        <CandidateRatingAreasSettings
          candidateRatingAreas={settings.company.candidateRatings}
          updateCandidateRatingAreas={updateCompany}
          id={settings.company._id}
        />
      ),
      icon: 'fa fa-star',
    },
    {
      label: 'Rating Weights',
      component: settings.company && (
        <CandidateRatingWeightsSettings
          candidateRatingWeight={settings.company.ratingWeight}
          updateCandidateRatingWeight={updateCompany}
          id={settings.company._id}
        />
      ),
      icon: 'fa fa-tasks',
    },
  ];

  if (userRole && !company?.testsOnly) anchorPoints.splice(4, 0, ...anchorPointsForNonTestsOnlyCompany);

  return (
    <Wrapper padding="25px 0 0 0" minHeight="100vh">
      <Container column>
        <TopHeadingContainer>
          <Header>Settings</Header>
          <AnchorLink href={`${config.REDIRECTION_URL}/gdpr-candidates`}>
            <Button primaryBlue primaryGreenBorder removeMargin>
              Candidates to be deleted
            </Button>
          </AnchorLink>
        </TopHeadingContainer>
        {settings?.company?.freeDemo && <DemoAccountBanner userRole={userRole} />}
        {fetchSettings && <Spinner />}
        <AnchorContent anchorPoints={anchorPoints} hideOnSmallDevice={true}></AnchorContent>
      </Container>
    </Wrapper>
  );
});
